import { cloneDeep } from "lodash-es";

import {
  getSources,
  type Medium,
  MediumField,
  type MediumName,
  social,
  type SocialPlatform,
} from "shared/helpers/media";
import {
  type Source,
  sourceGroupKeys,
  sourceKeysFor,
} from "shared/helpers/sourceFilters";
import type { SimpleBoolean } from "shared/resources/Stream";
import features from "shared/services/features";
import type { FilterSet, Nullable } from "shared/types";
import type { MentionType, SocialMentionType } from "shared/types/mentions";

import type {
  StreamFiltersAuthor,
  StreamFiltersCategory,
  StreamFiltersFrontPageRanks,
  StreamFiltersLicense,
  StreamFiltersMinMax,
  StreamFiltersTag,
  StreamReportFilters,
  StreamRequestFilters,
} from "./StreamFilters";

// Filter Sets need to be usable by all stream types
export type FilterSetOptionsFilters = StreamReportFilters &
  StreamRequestFilters;

export type InitialFilterSetFilters = Partial<
  Omit<FilterSetFilters, "filterSet">
>;

export default class FilterSetFilters {
  static get DEFAULT_FILTERS(): InitialFilterSetFilters {
    const filterSetFilters = new FilterSetFilters(null);

    return filterSetFilters.initialFilters;
  }

  advertisementRate: StreamFiltersMinMax;

  aggregateEmotionality: StreamFiltersMinMax;

  aggregateFakeness: StreamFiltersMinMax;

  aggregateHarmful: StreamFiltersMinMax;

  aggregateSpam: StreamFiltersMinMax;

  audience: StreamFiltersMinMax;

  authors: StreamFiltersAuthor[];

  booleanQuery: string;

  bundleBroadcast: boolean;

  categories: StreamFiltersCategory[];

  categoryKeys: string[];

  collapseSyndicated: boolean;

  desktopMonthlyVisits: StreamFiltersMinMax;

  domainAuthority: StreamFiltersMinMax;

  domainAuthorityRanges: StreamFiltersMinMax[];

  excludedAuthors: StreamFiltersAuthor[];

  excludedKeywords: string;

  excludedLanguageCodes: string[];

  excludedLicense: StreamFiltersLicense[];

  excludedLocationKeys: string[];

  excludedSources: Source[];

  excludedTags: StreamFiltersTag[];

  externalItemsEnabled: boolean;

  filterSet: Nullable<FilterSet>;

  frontPageRanks: StreamFiltersFrontPageRanks;

  frontPageRanksEnabled: boolean;

  impactScoreRanges: StreamFiltersMinMax[];

  includeBookmarkSyndication: boolean;

  includeInternationalPlus: Nullable<boolean>;

  includeNewswireContent: boolean;

  includeOnlineCaldSources: string;

  includePrintCaldSources: string;

  initialFilters: InitialFilterSetFilters;

  keywords: string;

  languageCodes: string[];

  license: StreamFiltersLicense[];

  locationKeys: string[];

  locations: number[];

  media: MediumName[];

  mediaTypes: MentionType[];

  minimumFollowerCount: number;

  minimumFollowerCountEnabled: boolean;

  mobileMonthlyVisits: StreamFiltersMinMax;

  onlyShowExternalItems: boolean;

  pageNumbers: StreamFiltersMinMax;

  pageNumbersEnabled: boolean;

  platforms: SocialPlatform[];

  programs: string[];

  reactScore: StreamFiltersMinMax;

  reactScoreAverageSelected: boolean;

  reactScoreBreakdown: boolean;

  selectedRadioContent: MentionType[];

  selectedSocialContent: SocialMentionType[];

  selectedTvContent: MentionType[];

  sentiment: StreamFiltersMinMax[];

  sentimentEnabled: boolean;

  simpleBoolean: Nullable<SimpleBoolean>;

  socialImpactThreshold: StreamFiltersMinMax;

  socialOnlyVerified: Nullable<boolean>;

  sources: Source[];

  stockSymbol?: string;

  tags: StreamFiltersTag[];

  unratedSentiment: boolean;

  useExactAuthors: boolean;

  verifiedEnabled: boolean;

  wordCount: StreamFiltersMinMax;

  wordCountEnabled: boolean;

  constructor(
    filterSet: Nullable<FilterSet>,
    initialFilters: InitialFilterSetFilters = {}
  ) {
    this.advertisementRate = { max: null, min: null };
    this.aggregateEmotionality = { max: 100, min: 0 };
    this.aggregateFakeness = { max: 100, min: 0 };
    this.aggregateHarmful = { max: 100, min: 0 };
    this.aggregateSpam = { max: 100, min: 0 };
    this.audience = { max: null, min: null };
    this.authors = [];
    this.booleanQuery = "";
    this.bundleBroadcast = false;
    this.categories = [];
    this.categoryKeys = [];
    this.collapseSyndicated = false;
    this.desktopMonthlyVisits = { max: null, min: null };
    this.domainAuthority = { max: null, min: null };
    this.domainAuthorityRanges = [];
    this.excludedAuthors = [];
    this.excludedLicense = [];
    this.excludedKeywords = "";
    this.excludedLanguageCodes = [];
    this.excludedLocationKeys = [];
    this.excludedSources = [];
    this.excludedTags = [];
    this.filterSet = filterSet;
    this.frontPageRanks = { min: 0 };
    this.frontPageRanksEnabled = false;
    this.impactScoreRanges = [];
    this.includeBookmarkSyndication = false;
    this.includeInternationalPlus = null;
    this.includeOnlineCaldSources = "";
    this.includePrintCaldSources = "";
    this.keywords = "";
    this.languageCodes = [];
    this.license = [];
    this.locationKeys = [];
    this.locations = [];
    this.media = [];
    this.mediaTypes = [];
    this.minimumFollowerCount = 500;
    this.minimumFollowerCountEnabled = false;
    this.mobileMonthlyVisits = { max: null, min: null };
    this.includeNewswireContent = false;
    this.onlyShowExternalItems = false;
    this.pageNumbers = { max: null, min: null };
    this.pageNumbersEnabled = false;
    this.platforms = [];
    this.programs = [];
    this.reactScore = { max: 100, min: 0 };
    this.reactScoreAverageSelected = true;
    this.reactScoreBreakdown = false;
    this.selectedRadioContent = [];
    this.selectedSocialContent = [];
    this.selectedTvContent = [];
    this.sentiment = [];
    this.sentimentEnabled = false;
    this.simpleBoolean = null;
    this.socialImpactThreshold = { max: null, min: null };
    this.socialOnlyVerified = null;

    this.sources = [];
    this.tags = [];
    this.unratedSentiment = false;
    this.useExactAuthors = false;
    this.verifiedEnabled = false;
    this.wordCount = { max: null, min: null };
    this.wordCountEnabled = false;

    if (
      this.canFilterExternalItems() &&
      !("externalItemsEnabled" in initialFilters)
    ) {
      this.externalItemsEnabled = true;
    }

    Object.assign(this, initialFilters);

    const filterSetFiltersClone = cloneDeep(this);

    const {
      filterSet: filterSetClone,
      initialFilters: initialFiltersClone,
      ...filters
    } = filterSetFiltersClone;

    this.initialFilters = cloneDeep(filters);
  }

  static fromOptions(
    filterSetOptions: FilterSetOptionsFilters,
    filterSet: Nullable<FilterSet>,
    initialFilters: InitialFilterSetFilters = {}
  ): FilterSetFilters {
    const {
      authors_filter: authors = [],
      boolean_query: booleanQuery = "",
      category_keys: categoryKeys = [],
      domain_authority: domainAuthorityRanges = [],
      excluded_authors_filter: excludedAuthors = [],
      excluded_keywords: excludedKeywords = "",
      excluded_language_codes: excludedLanguageCodes = [],
      excluded_license: excludedLicense = [],
      excluded_location_keys: excludedLocationKeys = [],
      impact_score: impactScoreRanges = [],
      include_external_items: externalItemsEnabled,
      include_international_plus: includeInternationalPlus = null,
      include_newswire_content: includeNewswireContent = false,
      include_online_cald_sources: includeOnlineCaldSources = "",
      include_print_cald_sources: includePrintCaldSources = "",
      keywords = "",
      language_codes: languageCodes = [],
      license = [],
      location_keys: locationKeys = [],
      max_advertisement_rate: maxAdvertisementRate = null,
      max_aggregate_emotionality: maxAggregateEmotinality,
      max_aggregate_fakeness: maxAggregateFakeness,
      max_aggregate_harmful: maxAggregateHarmful,
      max_aggregate_spam: maxAggregateSpam,
      max_audience: maxAudience = null,
      max_desktop_monthly_visits: maxDesktopMonthlyVisits = null,
      max_mobile_monthly_visits: maxMobileMonthlyVisits = null,
      max_react_score: maxReactScore = 1,
      max_social_impact_threshold: maxSocialImpactThreshold = null,
      max_word_count: maxWordCount = null,
      media = [],
      min_advertisement_rate: minAdvertisementRate = null,
      min_aggregate_emotionality: minAggregateEmotionality,
      min_aggregate_fakeness: minAggregateFakeness,
      min_aggregate_harmful: minAggregateHarmful,
      min_aggregate_spam: minAggregateSpam,
      min_audience: minAudience = null,
      min_desktop_monthly_visits: minDesktopMonthlyVisits = null,
      min_mobile_monthly_visits: minMobileMonthtlyVisits = null,
      min_react_score: minReactScore = 0,
      min_social_impact_threshold: minSocialImpactThreshold = null,
      min_word_count: minWordCount = null,
      only_external_items: onlyShowExternalItems = false,
      page_numbers: pageNumbers = { max: null, min: null },
      selected_radio_content: selectedRadioContent = [],
      selected_social_content: selectedSocialContent = [],
      selected_tv_content: selectedTvContent = [],
      sentiment = [],
      simple_boolean: simpleBoolean = null,
      social_only_verified: socialOnlyVerified = null,
      unrated_sentiment: unratedSentiment = false,
    } = filterSetOptions;

    const pageNumbersEnabled = Boolean(
      pageNumbers !== undefined &&
      (pageNumbers.min !== null || pageNumbers.max !== null)
    );

    const wordCountEnabled = Boolean(
      minWordCount !== null || maxWordCount !== null
    );

    const reactScoreAverageSelected = Boolean(
      maxAggregateEmotinality === undefined &&
      maxAggregateFakeness === undefined &&
      maxAggregateHarmful === undefined &&
      maxAggregateSpam === undefined
    );

    return new FilterSetFilters(filterSet, {
      ...initialFilters,
      advertisementRate: {
        max: maxAdvertisementRate !== null ? maxAdvertisementRate / 100 : null,
        min: minAdvertisementRate !== null ? minAdvertisementRate / 100 : null,
      },
      aggregateEmotionality: {
        max: maxAggregateEmotinality ? maxAggregateEmotinality * 100 : 100,
        min: minAggregateEmotionality ? minAggregateEmotionality * 100 : 0,
      },
      aggregateFakeness: {
        max: maxAggregateFakeness ? maxAggregateFakeness * 100 : 100,
        min: minAggregateFakeness ? minAggregateFakeness * 100 : 0,
      },
      aggregateHarmful: {
        max: maxAggregateHarmful ? maxAggregateHarmful * 100 : 100,
        min: minAggregateHarmful ? minAggregateHarmful * 100 : 0,
      },
      aggregateSpam: {
        max: maxAggregateSpam ? maxAggregateSpam * 100 : 100,
        min: minAggregateSpam ? minAggregateSpam * 100 : 0,
      },
      audience: { max: maxAudience, min: minAudience },
      authors,
      booleanQuery,
      categoryKeys,
      desktopMonthlyVisits: {
        max: maxDesktopMonthlyVisits,
        min: minDesktopMonthlyVisits,
      },
      domainAuthorityRanges,
      excludedAuthors,
      excludedKeywords,
      excludedLanguageCodes,
      excludedLicense,
      excludedLocationKeys,
      ...(externalItemsEnabled !== undefined ? { externalItemsEnabled } : {}),
      impactScoreRanges,
      includeInternationalPlus,
      includeNewswireContent,
      includeOnlineCaldSources,
      includePrintCaldSources,
      keywords,
      languageCodes,
      license,
      locationKeys,
      media,
      mobileMonthlyVisits: {
        max: maxMobileMonthlyVisits,
        min: minMobileMonthtlyVisits,
      },
      onlyShowExternalItems,
      pageNumbers,
      pageNumbersEnabled,
      reactScore: {
        max: maxReactScore ? maxReactScore * 100 : 100,
        min: minReactScore ? minReactScore * 100 : 0,
      },
      reactScoreAverageSelected,
      selectedRadioContent,
      selectedSocialContent,
      selectedTvContent,
      sentiment,
      sentimentEnabled: Boolean(sentiment.length),
      simpleBoolean,
      socialImpactThreshold: {
        max: maxSocialImpactThreshold,
        min: minSocialImpactThreshold,
      },
      socialOnlyVerified,
      unratedSentiment,
      wordCount: { max: maxWordCount, min: minWordCount },
      wordCountEnabled,
    });
  }

  appliedFilters(): string[] {
    const filtersUsed: string[] = [];

    if (this.keywordsFiltersCount()) filtersUsed.push("Keywords");
    filtersUsed.push(...this.mediaFilters());
    filtersUsed.push(...this.sourceFilters());
    filtersUsed.push(...this.contentFilters());
    filtersUsed.push(...this.metricFilters());
    filtersUsed.push(...this.otherFilters());

    return filtersUsed;
  }

  canFilterExternalItems(): boolean {
    return features.has("has_external_items");
  }

  clone(): FilterSetFilters {
    return Object.assign(
      new FilterSetFilters(this.filterSet, cloneDeep(this)),
      {
        initialFilters: cloneDeep(this.initialFilters),
      }
    );
  }

  contentFilters(): string[] {
    const filters: string[] = [];

    if (this.authors.length) filters.push("Authors");
    if (this.excludedAuthors.length) filters.push("Excluded Authors");
    if (this.excludedTags.length) filters.push("Excluded Tags");
    if (this.languageCodes.length) filters.push("Language Codes");
    if (this.excludedLanguageCodes.length)
      filters.push("Exclude Language Codes");
    if (this.license.length) filters.push("Content Types");
    if (this.pageNumbersEnabled) filters.push("Page Number");
    if (this.tags.length) filters.push("Tags");
    if (this.wordCountEnabled) filters.push("Word Count");

    return filters;
  }

  contentFiltersCount(): number {
    let count = 0;

    if (this.authors.length) count += 1;
    if (this.excludedAuthors.length) count += 1;
    if (this.excludedTags.length) count += 1;
    if (this.languageCodes.length) count += 1;
    if (this.excludedLanguageCodes.length) count += 1;
    if (this.license.length) count += 1;
    if (this.pageNumbersEnabled) count += 1;
    if (this.tags.length) count += 1;
    if (this.wordCountEnabled) count += 1;

    return count;
  }

  filteringAuthors(): boolean {
    return Boolean(this.authors.length);
  }

  filteringContent(
    { ignoredFilters }: { ignoredFilters: string[] } = { ignoredFilters: [] }
  ): boolean {
    const contentFilters = {
      excludedKeywords: this.excludedKeywords && this.excludedKeywords !== "",
      keywords: this.keywords && this.keywords !== "",
      pageNumber:
        this.pageNumbersEnabled !== this.initialFilters.pageNumbersEnabled,
      sentiment:
        this.sentimentEnabled !== this.initialFilters?.sentimentEnabled,
      wordCount: this.wordCountEnabled !== this.initialFilters.wordCountEnabled,
    };

    return Object.entries(contentFilters).some(
      ([filter, active]) => !ignoredFilters.includes(filter) && active
    );
  }

  filteringHandleOrSocialImpact(): boolean {
    const filters = [
      this.authors.length,
      this.verifiedEnabled !== this.initialFilters.verifiedEnabled,
      this.minimumFollowerCountEnabled !==
        this.initialFilters.minimumFollowerCountEnabled,
    ];

    return filters.some((filter) => filter);
  }

  filteringKeywords(): boolean {
    if (this.booleanQuery || this.keywords || this.excludedKeywords)
      return true;

    return false;
  }

  filteringLocations(): boolean {
    return Boolean(this.locations.length);
  }

  filteringMedia(defaultMedia: MediumName[]): boolean {
    return this.media.length
      ? defaultMedia.length - this.media.length > 0
      : false;
  }

  filteringPlatforms(): boolean {
    return Boolean(this.platforms.length);
  }

  filteringReactScore(): boolean {
    if (this.reactScoreBreakdown) {
      return true;
    }

    const reactScoreFilters = [
      this.reactScoreAverageSelected && this.scoreChanged(this.reactScore),
      !this.reactScoreAverageSelected &&
        this.scoreChanged(this.aggregateEmotionality),
      !this.reactScoreAverageSelected &&
        this.scoreChanged(this.aggregateFakeness),
      !this.reactScoreAverageSelected && this.scoreChanged(this.aggregateSpam),
      !this.reactScoreAverageSelected &&
        this.scoreChanged(this.aggregateHarmful),
    ];

    return reactScoreFilters.some((filter) => filter);
  }

  filteringSources(): boolean {
    const sourceFilters = [
      this.sources.length,
      this.excludedSources.length,
      this.categories.length,
    ];

    return sourceFilters.some((filter) => filter);
  }

  filteringTags(): boolean {
    const tagFilters = [this.tags.length, this.excludedTags.length];

    return tagFilters.some((filter) => filter);
  }

  keywordsFiltersCount(): number {
    let count = 0;
    if (this.booleanQuery) count += 1;
    if (this.keywords || this.excludedKeywords) count += 1;

    return count;
  }

  mediaCoverageFilters(medium: Medium): string[] {
    const filters: string[] = [];

    if (this.locationKeys.some((key) => key.includes(medium.field)))
      filters.push(`${medium.medium} Location`);

    if (this.excludedLocationKeys.some((key) => key.includes(medium.field)))
      filters.push(`Excluded ${medium.medium} Location`);

    if (this.categoryKeys.some((key) => key.includes(medium.field)))
      filters.push(`${medium.medium} Category`);

    if (medium.field === MediumField.ONLINE) {
      if (this.domainAuthorityRanges.length) filters.push("Domain Authority");

      if (this.includeInternationalPlus !== null)
        filters.push("International Plus");

      if (this.includeOnlineCaldSources && features.has("has_cald_sources"))
        filters.push("Online CALD Sources");
    }

    if (medium.field === MediumField.PRINT) {
      if (this.includePrintCaldSources && features.has("has_cald_sources"))
        filters.push("Print CALD Sources");
    }

    if (medium.field === MediumField.SOCIAL) {
      if (this.selectedSocialContent.length) filters.push("Social Content");

      if (this.validateMinOrMax(this.socialImpactThreshold))
        filters.push("Social Impact Threshold");

      if (
        this.socialOnlyVerified !== null &&
        this.filterSet?.social_only_verified !== this.socialOnlyVerified
      ) {
        filters.push("Social Verified Only");
      }
    }

    if (medium.field === MediumField.TV) {
      if (this.selectedTvContent.length) filters.push("TV Content");
    }

    if (medium.field === MediumField.RADIO) {
      if (this.selectedRadioContent.length) filters.push("Radio Content");
    }

    return filters;
  }

  mediaFilters(): string[] {
    if (this.filterSet && this.filterSet.enabled_media) {
      const filters: string[] = [];

      const enabledMedia = getSources().filter(({ medium }) =>
        this.filterSet!.enabled_media.includes(medium)
      );

      enabledMedia.forEach((medium) => {
        if (this.media.includes(medium.medium)) {
          filters.push(medium.medium);
        }

        filters.push(...this.mediaCoverageFilters(medium));
      });

      return filters;
    }

    return this.media;
  }

  mediaFiltersCount(): number {
    if (this.filterSet && this.filterSet.enabled_media) {
      const enabledMedia = getSources().filter(({ medium }) =>
        this.filterSet!.enabled_media.includes(medium)
      );

      if (this.media.length && enabledMedia.length !== this.media.length) {
        return this.media.length;
      }

      let count = 0;

      enabledMedia.forEach((medium) => {
        const coverageFilters = this.mediaCoverageFilters(medium);

        count += coverageFilters.length ? 1 : 0;
      });

      return count;
    }

    return 0;
  }

  merge(filters: InitialFilterSetFilters) {
    return Object.assign(this, filters);
  }

  metricFilters(): string[] {
    const filters: string[] = [];

    if (this.filteringReactScore()) filters.push("React Score");
    if (this.validateMinOrMax(this.advertisementRate))
      filters.push("Advertisement Rate");
    if (this.sentimentEnabled || this.unratedSentiment)
      filters.push("Sentiment");
    if (this.impactScoreRanges.length) filters.push("Impact Score");
    if (this.validateMinOrMax(this.audience)) filters.push("Audience");
    if (this.validateMinOrMax(this.mobileMonthlyVisits))
      filters.push("Mobile Monthly Visits");
    if (this.validateMinOrMax(this.desktopMonthlyVisits))
      filters.push("Desktop Monthly Visits");

    return filters;
  }

  metricFiltersCount(): number {
    let count = 0;

    if (this.filteringReactScore()) count += 1;
    if (this.validateMinOrMax(this.advertisementRate)) count += 1;
    if (this.sentimentEnabled || this.unratedSentiment) count += 1;
    if (this.impactScoreRanges.length) count += 1;

    if (
      this.validateMinOrMax(this.audience) ||
      this.validateMinOrMax(this.mobileMonthlyVisits) ||
      this.validateMinOrMax(this.desktopMonthlyVisits)
    ) {
      count += 1;
    }

    return count;
  }

  otherFilters(): string[] {
    const filters: string[] = [];

    if (this.canFilterExternalItems()) {
      if (!this.externalItemsEnabled) filters.push("Exclude External Items");
      if (this.onlyShowExternalItems) filters.push("Only show External Items");
    }

    if (this.includeNewswireContent) filters.push("Display Newswire Content");

    return filters;
  }

  otherFiltersCount(): number {
    let count = 0;

    if (
      this.canFilterExternalItems() &&
      (!this.externalItemsEnabled || this.onlyShowExternalItems)
    ) {
      count += 1;
    }

    if (this.includeNewswireContent) count += 1;

    return count;
  }

  platformFiltersCount(): number {
    return this.platforms.length;
  }

  platformsToMediaTypes(platforms: SocialPlatform[]): SocialMentionType[] {
    const mediaTypes: SocialMentionType[] = [];

    social.platforms.forEach((platform) => {
      if (platforms.includes(platform.platform)) {
        mediaTypes.push(...platform.fields);
      }
    });

    return mediaTypes;
  }

  rangeEquals(
    selected: StreamFiltersMinMax,
    initial: StreamFiltersMinMax
  ): boolean {
    return selected.min === initial.min && selected.max === initial.max;
  }

  requestFilters(): FilterSetOptionsFilters {
    const params = <FilterSetOptionsFilters>{};

    if (this.keywords !== "") params.keywords = this.keywords;
    if (this.excludedKeywords !== "")
      params.excluded_keywords = this.excludedKeywords;
    if (this.booleanQuery !== "") params.boolean_query = this.booleanQuery;

    if (this.sources?.length) {
      params.source_keys = sourceKeysFor(this.sources);
      const includedSourceGroupKeys = sourceGroupKeys(this.sources);

      if (includedSourceGroupKeys.length)
        params.source_group_keys = sourceGroupKeys(this.sources);
    }

    if (this.excludedSources?.length > 0) {
      params.excluded_source_keys = sourceKeysFor(this.excludedSources);
      const excludedSourceGroupKeys = sourceGroupKeys(this.excludedSources);
      if (excludedSourceGroupKeys.length)
        params.excluded_source_group_keys = excludedSourceGroupKeys;
    }

    if (this.tags.length > 0) {
      const tags = this.tags.map((tag) => tag.id);

      params.bookmark_stream_ids = tags;
    }

    if (this.excludedTags?.length) {
      const excludedTags = this.excludedTags.map(
        (excludedTag) => excludedTag.id
      );

      params.excluded_bookmark_stream_ids = excludedTags;
    }

    if (this.domainAuthorityRanges.length) {
      params.domain_authority = this.domainAuthorityRanges;
    }

    if (this.authors.length) {
      params.authors = this.authors
        .map((author) => author.name || author)
        .join(", ");
    }

    if (this.excludedAuthors.length) {
      params.excluded_authors = this.excludedAuthors
        .map((author) => author.name || author)
        .join(", ");
    }

    if (this.wordCountEnabled && this.validateMinOrMax(this.wordCount)) {
      if (this.wordCount.min !== null)
        params.min_word_count = this.wordCount.min;
      if (this.wordCount.max !== null)
        params.max_word_count = this.wordCount.max;
    }

    params.include_external_items = this.externalItemsEnabled;

    if (this.externalItemsEnabled) {
      params.only_external_items = this.onlyShowExternalItems;
    }

    params.include_newswire_content = this.includeNewswireContent;

    if (this.locationKeys.length) {
      params.location_keys = this.locationKeys;
    }

    if (this.excludedLocationKeys.length) {
      params.excluded_location_keys = this.excludedLocationKeys;
    }

    if (this.sentimentEnabled) {
      params.sentiment = this.sentiment;
    }

    if (this.unratedSentiment) {
      params.unrated_sentiment = this.unratedSentiment;
    }

    if (this.media.length > 0) params.media = this.media;

    if (this.categoryKeys.length) {
      params.category_keys = this.categoryKeys;
    }

    if (this.pageNumbersEnabled && this.validateMinOrMax(this.pageNumbers)) {
      params.page_numbers = this.pageNumbers;
    }

    if (this.filteringReactScore()) {
      if (
        this.reactScoreAverageSelected &&
        this.validateMinMax(this.reactScore)
      ) {
        params.min_react_score = this.reactScore.min / 100;
        params.max_react_score = this.reactScore.max / 100;
      } else {
        if (this.validateMinMax(this.aggregateEmotionality)) {
          params.min_aggregate_emotionality =
            this.aggregateEmotionality.min / 100;

          params.max_aggregate_emotionality =
            this.aggregateEmotionality.max / 100;
        }

        if (this.validateMinMax(this.aggregateFakeness)) {
          params.min_aggregate_fakeness = this.aggregateFakeness.min / 100;
          params.max_aggregate_fakeness = this.aggregateFakeness.max / 100;
        }

        if (this.validateMinMax(this.aggregateSpam)) {
          params.min_aggregate_spam = this.aggregateSpam.min / 100;
          params.max_aggregate_spam = this.aggregateSpam.max / 100;
        }

        if (this.validateMinMax(this.aggregateHarmful)) {
          params.min_aggregate_harmful = this.aggregateHarmful.min / 100;
          params.max_aggregate_harmful = this.aggregateHarmful.max / 100;
        }
      }
    }

    if (this.languageCodes.length) {
      params.language_codes = this.languageCodes;
    }

    if (this.excludedLanguageCodes.length) {
      params.excluded_language_codes = this.excludedLanguageCodes;
    }

    if (this.impactScoreRanges.length) {
      params.impact_score = this.impactScoreRanges;
    }

    if (this.validateMinOrMax(this.advertisementRate)) {
      if (this.advertisementRate.min !== null)
        params.min_advertisement_rate = this.advertisementRate.min * 100;
      if (this.advertisementRate.max !== null)
        params.max_advertisement_rate = this.advertisementRate.max * 100;
    }

    if (this.validateMinOrMax(this.audience)) {
      if (this.audience.min !== null) params.min_audience = this.audience.min;
      if (this.audience.max !== null) params.max_audience = this.audience.max;
    }

    if (this.validateMinOrMax(this.desktopMonthlyVisits)) {
      if (this.desktopMonthlyVisits.min !== null)
        params.min_desktop_monthly_visits = this.desktopMonthlyVisits.min;
      if (this.desktopMonthlyVisits.max !== null)
        params.max_desktop_monthly_visits = this.desktopMonthlyVisits.max;
    }

    if (this.validateMinOrMax(this.mobileMonthlyVisits)) {
      if (this.mobileMonthlyVisits.min !== null)
        params.min_mobile_monthly_visits = this.mobileMonthlyVisits.min;
      if (this.mobileMonthlyVisits.max !== null)
        params.max_mobile_monthly_visits = this.mobileMonthlyVisits.max;
    }

    if (this.license.length) {
      params.license = this.license;
    }

    if (this.excludedLicense.length) {
      params.excluded_license = this.excludedLicense;
    }

    if (this.includeInternationalPlus !== null) {
      params.include_international_plus = this.includeInternationalPlus;
    }

    if (this.includeOnlineCaldSources) {
      params.include_online_cald_sources = this.includeOnlineCaldSources;
    }

    if (this.includePrintCaldSources) {
      params.include_print_cald_sources = this.includePrintCaldSources;
    }

    if (this.selectedTvContent.length) {
      params.selected_tv_content = this.selectedTvContent;
    }

    if (this.selectedRadioContent.length) {
      params.selected_radio_content = this.selectedRadioContent;
    }

    if (this.selectedSocialContent.length) {
      params.selected_social_content = this.selectedSocialContent;
    }

    if (this.validateMinOrMax(this.socialImpactThreshold)) {
      if (this.socialImpactThreshold.min !== null)
        params.min_social_impact_threshold = this.socialImpactThreshold.min;
      if (this.socialImpactThreshold.max !== null)
        params.max_social_impact_threshold = this.socialImpactThreshold.max;
    }

    if (this.socialOnlyVerified !== null) {
      params.social_only_verified = this.socialOnlyVerified;
    }

    return params;
  }

  scoreChanged(score: StreamFiltersMinMax): boolean {
    return score.min !== 0 || score.max !== 100;
  }

  sourceFilters(): string[] {
    const filters: string[] = [];
    if (this.sources.length) filters.push("Included Sources");
    if (this.excludedSources.length) filters.push("Excluded Sources");

    return filters;
  }

  sourcesFiltersCount(): number {
    return this.sources.length + this.excludedSources.length;
  }

  toOptions(): FilterSetOptionsFilters {
    const options = <FilterSetOptionsFilters>{};

    if (this.authors.length) {
      options.authors_filter = this.authors;
    }

    if (this.excludedAuthors.length) {
      options.excluded_authors_filter = this.excludedAuthors;
    }

    if (this.simpleBoolean) {
      options.simple_boolean = this.simpleBoolean;
    }

    const filters = this.requestFilters();

    return {
      ...options,
      ...filters,
    };
  }

  totalFilters(): number {
    return (
      this.contentFiltersCount() +
      this.keywordsFiltersCount() +
      this.mediaFiltersCount() +
      this.metricFiltersCount() +
      this.otherFiltersCount() +
      this.sourcesFiltersCount() +
      this.platformFiltersCount()
    );
  }

  validateMinMax(
    minMax: StreamFiltersMinMax
  ): minMax is { [K in keyof StreamFiltersMinMax]: number } {
    return minMax.max !== null && minMax.min !== null;
  }

  validateMinOrMax(
    minMax: StreamFiltersMinMax
  ): minMax is { [K in keyof StreamFiltersMinMax]: number } {
    return minMax.max !== null || minMax.min !== null;
  }
}
